import Decimal from 'decimal.js'

import {
  DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID,
  IgnoredXeroServiceCodes,
} from 'constants/index'
import {
  GetBookingQuery,
  GetUserBookingQuery,
  GoogleAddressDtoInput,
  XeroServiceType,
} from 'generated/graphql'
import {
  TBookingAddressForm,
  TBookingChargeForm,
  TBookingForm,
  TBookingPackageForm,
} from 'types/form-types'

interface IDriver {
  charge: number
  extraPayment: number
}

export const getTotalDriverCharge = (drivers: Array<IDriver> | undefined) => {
  if (!drivers) {
    return 0
  }

  if (drivers.length < 1) {
    return 0
  }

  return drivers.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.charge + currentValue.extraPayment,
    0,
  )
}

export const getOtherChargesValue = (charges: Array<any> | undefined | null) => {
  if (!charges) {
    return 0
  }
  return charges
    .filter((c) => !c.isDeleted)
    .reduce(
      (previousValue, currentValue) => currentValue.rate * currentValue.quantity + previousValue,
      0,
    )
}

export const getBookingRevenue = (consignmentFee: number, otherCharges: number) =>
  new Decimal(consignmentFee).plus(otherCharges).toNumber()

export const getBookingMargin = (revenue: number, totalDriverCharge: number) => {
  if (revenue === 0 && totalDriverCharge === 0) {
    return 0
  }

  return new Decimal(revenue).minus(totalDriverCharge).div(revenue).toNumber()
}

export const getGoogleAddressInput = (
  collection: Array<any>,
  delivery: Array<any>,
): Array<GoogleAddressDtoInput> =>
  [...collection, ...delivery]
    .map((address) => {
      if (address.name || address.city || address.addressLine1 || address.postcode) {
        return {
          sequenceOrder: address.sequenceOrder,
          addressLine1: address.addressLine1 || '',
          ...(address.addressLine2 && {
            addressLine2: address.addressLine2,
          }),
          postcode: address.postcode,
          city: address.city || '',
          countryId: address.countryId,
        }
      }
    })
    .filter((address): address is GoogleAddressDtoInput => !!address)

type AddressMatchInput = {
  addressLine1?: string | null
  postcode?: string | null
  googleMapsPlaceId?: string | null
}

const normalizeAddressPart = (value?: string | null): string =>
  (value ?? '').replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase()

const normalizePostcode = (value?: string | null): string =>
  (value ?? '').replace(/\s+/g, '').toUpperCase()

// Same location if both Place IDs match; otherwise fall back to a case/whitespace-
// insensitive match on address line 1 + postcode (Place IDs arrive async, see applyPlaceIds).
export const isSameBookingAddress = (a?: AddressMatchInput, b?: AddressMatchInput): boolean => {
  if (!a || !b) {
    return false
  }

  if (a.googleMapsPlaceId && b.googleMapsPlaceId) {
    return a.googleMapsPlaceId === b.googleMapsPlaceId
  }

  return (
    normalizeAddressPart(a.addressLine1) === normalizeAddressPart(b.addressLine1) &&
    normalizePostcode(a.postcode) === normalizePostcode(b.postcode)
  )
}

// Finds the Wait & Return stop among the delivery addresses.
// The return stop is the last delivery that matches the collection location
// and may appear anywhere in the route, not necessarily as the final stop.
// Returns -1 if no matching return stop is found.
export const findReturnStopIndex = (
  deliveryAddresses: Array<AddressMatchInput> | undefined,
  collectionAddress?: AddressMatchInput,
): number => {
  if (!deliveryAddresses?.length || !collectionAddress) {
    return -1
  }

  for (let index = deliveryAddresses.length - 1; index >= 0; index--) {
    if (isSameBookingAddress(deliveryAddresses[index], collectionAddress)) {
      return index
    }
  }

  return -1
}

// Builds the address pair for the leg leading into the Wait & Return stop.
// This leg represents the distance excluded from the Wait & Return pricing.
// Returns null if no return stop exists or the leg cannot be constructed.
export const getReturnLegAddressInput = (
  collection: Array<any>,
  delivery: Array<any>,
): Array<GoogleAddressDtoInput> | null => {
  const returnIndex = findReturnStopIndex(delivery, collection?.[0])
  if (returnIndex === -1) {
    return null
  }

  const route = [...collection, ...delivery]
  const position = collection.length + returnIndex
  const leg = getGoogleAddressInput([route[position - 1]], [route[position]])

  return leg.length === 2 ? leg : null
}

export const isMultiDrop = (bookingDetails: TBookingForm) =>
  bookingDetails.collectionAddresses.length + bookingDetails.deliveryAddresses.length !== 2
export const ignoreServiceType = (serviceType: XeroServiceType | undefined) =>
  !serviceType || IgnoredXeroServiceCodes.includes(serviceType)

export const mapToBookingAddressForm = (addressEntry: any): TBookingAddressForm => ({
  at: '',
  by: '',
  name: addressEntry.address.name,
  city: addressEntry.address.city,
  addressLine1: addressEntry.address.addressLine1,
  addressLine2: addressEntry.address.addressLine2 ?? '',
  postcode: addressEntry.address.postcode,
  countryId: Number(addressEntry.address.countryId),
  contact: addressEntry.address.contact ?? '',
  email: addressEntry.address.email ?? '',
  telephoneNumber: addressEntry.address.telephoneNumber ?? '',
  note: addressEntry.note,
  sequenceOrder: addressEntry.sequenceOrder,
  isVerified: addressEntry.address.isVerified,
  reasonCodeId: Number(addressEntry.reasonCodeId),
  googleMapsPlaceId: addressEntry.address.googleMapsPlaceId ?? '',
})

export const mapToBookingPackageForm = (pkg: any): TBookingPackageForm => ({
  quantity: pkg.quantity,
  packageType: pkg.type,
  collectionSequenceOrder: pkg.collectionSequenceOrder,
  deliverySequenceOrder: pkg.deliverySequenceOrder,
  length: pkg.length,
  width: pkg.width,
  height: pkg.height,
  weight: pkg.weight,
  reference: pkg.packageReference,
  details: pkg.name,
})

export const mapToBookingChargeForm = (charge: any): TBookingChargeForm => ({
  name: charge.name,
  type: charge.type,
  quantity: charge.quantity,
  rate: charge.rate,
  description: charge.description,
  xeroServiceCode: charge.xeroServiceCode,
  vatCategoryId: String(charge.vatCategoryId),
  isDeleted: charge.isDeleted,
  isAutomatic: charge.isAutomatic,
})

export const getWaitAndReturnFeeCategoryId = (
  booking:
    | NonNullable<GetBookingQuery['booking']>
    | NonNullable<GetUserBookingQuery['userBooking']>,
) => {
  const bookingValue = booking.waitAndReturnFeeCategoryId
  const customerValue = booking.customer?.waitAndReturnFeeCategoryId

  const currentValue =
    bookingValue ?? customerValue ?? DEFAULT_CUSTOMER_WAIT_AND_RETURN_FEE_CATEGORY_ID

  return currentValue != null ? String(currentValue) : ''
}
